import * as types from "../../../../constants";
import React, {useState, useEffect, FC} from "react";
import {
  Box,
  Card as MuiCard,
  CardContent,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  withStyles,
  Button, MenuItem,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import {
  createUnit,
  updateUnit,
  fetchUnitLevels,
  fetchUnits,
} from "../../../../redux/actions/unitsActions";
import { AppStateType } from "../../../../redux/reducers";
import { createUnit as createUnitService } from "../../../../services/unitsService";
import { useRouteMatch } from "react-router";
import { useParams } from "react-router-dom";
import { getURL, urls } from "../../../../routes";
import { GoToClassify } from "../../../../components/GoToClassify";
import {
  AccessibilityUnit,
  AccessibilityUnitLevel,
} from "../../../../models/AccessibilityUnits";
import { Property } from "../../../../models/Property";
import { CurrentUnit } from "../../../../components/contexts/UnitsContext";
import { AppDispatchType } from "../../../../redux/store";
import { useTranslation } from "react-i18next";
import HospitalityUnitDetails from "./components/CompositeUnits";
import FloorList from "./components/FloorList";
import UnitMap from "./components/UnitMap";
import CompositeUnits from "./components/CompositeUnits";
import AccordionMultiLanguageInputs from "../../../../components/AccordionMultiLanguageInputs";
import {correctionUnitName, getDefaultLanguagesObject, getTextByCurrentLanguage} from "../../../../utils/functions";

const Title = styled(Typography)`
    ${(props) => props.theme.breakpoints.down("sm")} {
      font-size: 14px;
    }
`

const StyledButton = withStyles({
  root: {
    color: "black",
    background: "linear-gradient(90deg, #84FAB0 0%, #8FEAFE 100%)",
  },
})(Button);
const Card = styled(MuiCard)(spacing);


export const helperText = (t: any, key: string) => {
  const texts: any = {
    entrance: t("unitForm_helperText_entrance"),
    Lobby: t("unitForm_helperText_lobby"),
    lobby: t("unitForm_helperText_lobby"),
    reception_infodesk: t("unitForm_helperText_reception"),
    facility: t("unitForm_helperText_facility"),
    parking: t("unitForm_helperText_parking"),
    meeting_room: t("unitForm_helperText_conferenceRoom"),
    auditorium: t("unitForm_helperText_auditorium"),
    dining_area: t("unitForm_helperText_diningArea"),
    toilet_room__common: t("unitForm_helperText_toiletRoom"),
    gym: t("unitForm_helperText_gym"),
    spa: t("unitForm_helperText_spa"),
    hospitality_room_type: t("unitForm_helperText_hospitalityRoom"),
    shopping_aisle: t("unitForm_helperText_shopping_aisle"),
    counter: t("unitForm_helperText_counter"),
    checkout_pay: t("unitForm_helperText_checkout_pay"),
    fitting_room: t("unitForm_helperText_fitting_room"),
    manned_kiosk: t("unitForm_helperText_manned_kiosk"),
    floor: t("unitForm_helperText_floor"),
    playground: t("unitForm_helperText_playground"),
    garden: t("unitForm_helperText_garden"),
    bathroom_common: t("unitForm_helperText_bathroom_common"),
    bathroom__private: t("unitForm_helperText_bathroom__private"),
    quiet_room: t("unitForm_helperText_quiet_room"),
    kitchen_common: t("unitForm_helperText_kitchen_common"),
    toilet_room__private: t("unitForm_helperText_toilet_room__private"),
    kitchen__private: t("unitForm_helperText_kitchen__private"),
  };

  if (texts[key]) {
    return texts[key];
  }
  return "";
};

// FAQ for UnitSettings
// is_composite_unit_available - show/hide composite units block
// is_unit_quantity_available = show/hide quantity block
// is_floor_level_available = show/hide floor level
// is_map_available = show/hide map
// is_considered_unit_available = show/hide checkbox considered_unit

type UnitSettings = {
  is_composite_unit_available: boolean
  is_unit_quantity_available: boolean
  is_floor_level_available: boolean
  is_map_available: boolean
  is_considered_unit_available: boolean
}

type PropsType = {
  currentUnit: CurrentUnit;
  existingUnit: AccessibilityUnit | null;
  setExistingUnit: React.Dispatch<AccessibilityUnit>;
};

export const UnitForm: FC<PropsType> = ({
  currentUnit,
  existingUnit,
  setExistingUnit,
}) => {
  const { url } = useRouteMatch();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [unitName, setUnitName] = useState(
    existingUnit ? existingUnit.name : {en: ''}
  );
  const [unitAccessible, setUnitAccessible] = useState(
    existingUnit ? existingUnit.is_accessible_unit : false
  );
  const [floors, setFloors] = useState(1);
  const [levels, setLevels] = useState<AccessibilityUnitLevel[]>([
    { level: 0, number_of_units: null },
  ]);
  const [loading, setLoading] = useState(false);
  const units: AccessibilityUnit[] = useSelector(
    (state: AppStateType) => state.unitsReducer.units
  );
  const unit_levels = useSelector(
    (state: AppStateType) => state.unitsReducer?.unit_levels
  );
  const property: any = useSelector(
    (state: AppStateType) => state.propertyReducer.property
  )!;
  const user = useSelector((state: AppStateType) => state.authReducer.user)!;
  const a11yStandard = useSelector((state: AppStateType) =>
    state.a11yStandardReducer.a11yStandard.find((item: any) => {
      return item.key === property.a11y_standard;
    })
  );
  const [coordinates, setCoordinates] = useState<number[]>([0,0])
  const [compositeUnits, setCompositeUnits] = useState<any>([])
  const [addPoint, setAddPoint] = useState<boolean>(false)
  const [unitSettings, setUnitSettings] = useState<UnitSettings | null>(null)
  const company = useSelector((state: AppStateType) => state.companyReducer.company);
  const DEFAULT_UNIT_NAME = getDefaultLanguagesObject(company ? company.supported_languages : [])

  useEffect(() => {
    if (currentUnit && a11yStandard) {
      const findA11yUnit = a11yStandard.values.find((i: any) => i.key === currentUnit.type)
      if (findA11yUnit) {
        setUnitSettings(findA11yUnit.unit_details)
      }
    }
  }, [currentUnit]);

  useEffect(() => {
    if (currentUnit.type === 'facility' && !existingUnit) {
      setUnitName({
        ...DEFAULT_UNIT_NAME,
        en: property.name
      })
    } else {
      setUnitName(existingUnit ? existingUnit.name : DEFAULT_UNIT_NAME);
    }
    setUnitAccessible(existingUnit ? existingUnit.is_accessible_unit : true);
    if (existingUnit && existingUnit.id) {
      dispatch(fetchUnitLevels(existingUnit.id, user.token));
    } else {
      if (!unitSettings?.is_unit_quantity_available) {
        setLevels([{ level: 0, number_of_units: 1 }]);
      } else {
        setLevels([{ level: 0, number_of_units: null }]);
      }
    }
    return () => {
      setFloors(1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingUnit, unitSettings]);

  useEffect(() => {
    if (unit_levels && existingUnit) {
      setLevels(unit_levels);
      setFloors(unit_levels.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit_levels]);

  useEffect(() => {
    if (existingUnit && existingUnit.point && existingUnit.point.coordinates.length > 0) {
      setCoordinates(existingUnit.point.coordinates)
      setAddPoint(true)
    } else {
      setAddPoint(false)
      if (property && property.point) {
        const {point: { coordinates }} = property
        setCoordinates([coordinates[0], coordinates[1]])
      }
    }
  }, [property, existingUnit]);

  const handleFloor = () => {
    setLevels([...levels, { level: 0, number_of_units: 0 }]);
    setFloors((prevState) => {
      return prevState + 1;
    });
  };
  const handleUnitName = (text: string, language: string) => {
    setUnitName({
      ...unitName,
      [language]: text
    });
  };
  const handleUnitAccessible = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUnitAccessible(e.target.checked);
  };

  const handleChangeLevels = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    if (property && property.a11y_standard !== "hotel") {
      setLevels(
        levels.map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                [`${e.target.name}`]: e.target.value,
                number_of_units: e.target.value as any,
              }
            : item
        )
      );
    } else {
      setLevels(
        levels.map((item, itemIndex) =>
          itemIndex === index
            ? { ...item, [`${e.target.name}`]: e.target.value }
            : item
        )
      );
    }
  };

  const handleChangeCounter = (field: string, value: number, index: number) => {
    setLevels(
      levels.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  }

  const handleDeleteLevels = (index: number) => {
    if (floors > 1) {
      setLevels(levels.filter((i, indexItem) => indexItem !== index));
      setFloors(floors - 1);
    }
  };

  const isEqualsCoordinates = (property: Property, coordinates: number[]) => {
    if (!addPoint) {
      return true
    }
    if (property && property?.point?.coordinates) {
      const propertyCord = property.point.coordinates
      return propertyCord[0] === coordinates[0] && propertyCord[1] === coordinates[1]
    }
    return false
  }

  const clearFields = () => {
    setUnitName(getDefaultLanguagesObject(company ? company.supported_languages : []));
    setUnitAccessible(false);
    setFloors(1);
    setLevels([{ level: 0, number_of_units: null }]);
    setAddPoint(false)
  }

  const isValid = () => {
    let validLevels = true
    let unitNames = true
    if (unitSettings?.is_floor_level_available && unitSettings?.is_unit_quantity_available) {
      validLevels = levels.every((l) => (
        l.level !== null && l.number_of_units !== null && Number(l.number_of_units) !== 0
      ));
    } else if (unitSettings?.is_floor_level_available) {
      validLevels = levels.every((l) => (
        l.level !== null
      ));
    } else if (unitSettings?.is_unit_quantity_available) {
      validLevels = levels.every((l) => (
        l.number_of_units !== null && Number(l.number_of_units) !== 0
      ));
    }
    unitNames = Object.values(unitName).some((i) => i !== '')

    return unitNames && validLevels && (!addPoint || !isEqualsCoordinates(property, coordinates))
  };

  const onSave = async () => {
    setLoading(true);
    if (isValid()) {
      if (compositeUnits.length > 0) {
        let preparedCompositeUnits: any[] = []
        compositeUnits.forEach((unit: any) => {
          if (unit.selected) {
            const title = unit.title === "Floor level change" ? 'Access' : unit.title
            const preparedUnit: any = {
              name: title,
              type: unit.key,
              property: property.id,
              is_accessible_unit: unitAccessible,
              accessibility_unit_levels: levels,
            }
            if (!isEqualsCoordinates(property, coordinates)) {
              preparedUnit.point = { type: 'Point', coordinates: coordinates }
            }
            preparedCompositeUnits.push(preparedUnit)
          }
        })
        if (existingUnit) {
          setLoading(true);
          const data: any = {
            name: correctionUnitName(unitName),
            type: existingUnit.type,
            property: property.id,
            is_accessible_unit: unitAccessible,
            accessibility_unit_levels: levels,
          };
          if (!isEqualsCoordinates(property, coordinates)) {
            data.point = {
              type: 'Point',
              coordinates: coordinates
            }
          }
          const unitIndex = units.findIndex(
            (unit) => unit.id === existingUnit.id
          );
          dispatch(async (dp: AppDispatchType) => {
            const newUnit = (
              await updateUnit(data, user.token, existingUnit.id, unitIndex)(dp)
            ).data;
            setExistingUnit(newUnit);
            await fetchUnits(property.id, user.token)(dp);
            setLoading(false);
          });

        } else {
          const data: any = {
            name: correctionUnitName(unitName),
            type: currentUnit.type,
            property: property.id,
            is_accessible_unit: unitAccessible,
            accessibility_unit_levels: levels,
          };
          if (!isEqualsCoordinates(property, coordinates)) {
            data.point = {
              type: 'Point',
              coordinates: coordinates
            }
          }
          await createUnitService(data, user.token).then((response: any) => {
            dispatch({
              type: types.CREATE_UNIT,
              unit: response.data,
            });

            const changeText = (texts: any, name: string) => {
              const changedTexts = JSON.parse(JSON.stringify(texts))
              for (let lang in changedTexts) {
                if (changedTexts[lang]) {
                  changedTexts[lang] += ` - ${name}`;
                }
              }
              return changedTexts
            }

            preparedCompositeUnits = preparedCompositeUnits.map((i) => (
              {
                ...i,
                name: changeText(response.data.name, i.name),
                related_to: [response.data.id]
              }
            ))
            preparedCompositeUnits.forEach((i) => (
              dispatch(createUnit(i, user.token))
            ))
          });
          clearFields()
          setLoading(false);
        }
      } else if (currentUnit && !existingUnit) {
        const data: any = {
          name: correctionUnitName(unitName),
          type: currentUnit.type,
          property: property.id,
          is_accessible_unit: unitAccessible,
          accessibility_unit_levels: levels,
        };
        if (!isEqualsCoordinates(property, coordinates)) {
          data.point = {
            type: 'Point',
            coordinates: coordinates
          }
        }
        dispatch(createUnit(data, user.token));
        clearFields()
        setLoading(false)
      } else if (existingUnit && units) {
        const data: any = {
          name: correctionUnitName(unitName),
          type: existingUnit.type,
          property: property.id,
          is_accessible_unit: unitAccessible,
          accessibility_unit_levels: levels,
        };
        if (!isEqualsCoordinates(property, coordinates)) {
          data.point = {
            type: 'Point',
            coordinates: coordinates
          }
        }
        const unitIndex = units.findIndex((unit) => unit.id === existingUnit.id);
        dispatch(async (dp: AppDispatchType) => {
          const newUnit = (
            await updateUnit(data, user.token, existingUnit.id, unitIndex)(dp)
          ).data;
          setExistingUnit(newUnit);
          await fetchUnits(property.id, user.token)(dp);
          setLoading(false);
        });
      }
    }
  };

  const onDragEnd = async (val: any) => {
    setCoordinates([val.center.lng(), val.center.lat()])
  }

  const getCompositeUnits = (a11yStandard: any) => {
    const compositeUnits: any = []
    if (currentUnit) {
      a11yStandard.values.forEach((value: any) => {
        const findItem = value.parents.find((p: any) => p.unit_key === currentUnit.type)
        if (findItem) {
          value.selected = findItem.is_mandatory
          compositeUnits.push(value)
        }
      })
      setCompositeUnits(compositeUnits)
    }
  }

  useEffect(() => {
    if (currentUnit.type) {

      getCompositeUnits(a11yStandard)
    }
  }, [currentUnit]);

  const getTitle = () => {
    const selectUnitType = !!existingUnit
      ? existingUnit.type
      : currentUnit.type;
    const unit = a11yStandard.values.find(
      (item: any) => selectUnitType === item.key
    );
    const title = unit ? unit.title : "";
    return `${t("unitForm_unitTypeDetails")} ${title}`;
  };

  const handleDeletePoint = () => {
    if (existingUnit) {
      const unitIndex = units.findIndex((unit) => unit.id === existingUnit.id);
      if (unitIndex !== -1) {
        dispatch(async (dp: AppDispatchType) => {
          const newUnit = (
            await updateUnit({point: null}, user.token, existingUnit.id, unitIndex)(dp)
          );
          if (newUnit) {
            setExistingUnit(newUnit.data)
          }
          await fetchUnits(property.id, user.token)(dp);
          setLoading(false);
        });
      }
    }
  }

  return (
    <div>
      <Card>
        <CardContent>
          <Title variant="h6" gutterBottom>
            {getTitle()}
          </Title>
          <Box my={5}>
            <AccordionMultiLanguageInputs
              label={"FAR_TableUnitName"}
              placeholder={t("unitForm_unitName")}
              onChange={handleUnitName}
              value={unitName}
              helperText={helperText(t, existingUnit ? existingUnit.type : currentUnit.type)}
            />
          </Box>
          {unitSettings?.is_floor_level_available || unitSettings?.is_unit_quantity_available ? (
            <FloorList
              floors={floors}
              handleClick={handleFloor}
              levels={levels}
              handleDelete={handleDeleteLevels}
              property={property}
              handleChangeCounter={handleChangeCounter}
              isQuantityAvailable={!!unitSettings?.is_unit_quantity_available}
              isFloorAvailable={!!unitSettings?.is_floor_level_available}
            />
          ) : null}
          {unitSettings?.is_composite_unit_available && compositeUnits.length > 0 ? (
            <CompositeUnits
              existingUnit={existingUnit}
              units={units}
              compositeUnits={compositeUnits}
              handleCompositeUnits={setCompositeUnits}
            />
          ) : null}
          {unitSettings?.is_map_available ? (
            <Box my={5}>
              <UnitMap
                addPoint={addPoint}
                onSetAddPoint={setAddPoint}
                coordinates={coordinates}
                onDragEnd={onDragEnd}
                isExistPoint={existingUnit ? !!existingUnit.point : false}
                onDeletePoint={handleDeletePoint}
              />
            </Box>
          ) : null}
          {unitSettings?.is_considered_unit_available ? (
            <FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={unitAccessible}
                    onChange={handleUnitAccessible}
                  />
                }
                label={t("unitForm_accessibleUnit")}
              />
            </FormControl>
          ) : null}

          <Box mt={5}>
            <StyledButton
              disabled={!isValid() || loading}
              onClick={onSave}
              style={{
                color: !isValid() || loading ? "#929292" : "black",
                background:
                  isValid() || loading
                    ? "1px solid #929292"
                    : "1px solid black",
              }}
            >
              {t("questionUnits_unitExtraInfoSave_button")}
            </StyledButton>
          </Box>
        </CardContent>
      </Card>
      {url === getURL(urls.editFacilities, { id: property ? property.id : id }) ? (
        <GoToClassify id={property ? property.id : id} />
      ) : null}
    </div>
  );
};
